// Port of BivouacEngine/EngineSupport.swift: internal helpers shared by the zone generator and the Sleep Tonight
// planner (terrain lattice, polygon overlap, weather adjustment, POI proximity, overnight facilities, confidence and
// sensitivity ranks, `RouteTrack.slice`). Also holds the value equality of reasons (Swift `Hashable`).

import type {
  BivouacReason,
  BivouacReasonKind,
  Confidence,
  EpochMs,
  GeoCoordinate,
  GeoPolygon,
  POICategory,
  PointOfInterest,
  RoutePoint,
  Sensitivity,
  WeatherForecast,
  WeatherSnapshot,
} from '../core/models';
import {
  distance,
  isValidCoordinate,
  localXY,
  metersPerDegreeLatitude,
  metersPerDegreeLongitude,
  openRing,
  polygonContains,
} from '../core/geo';
import { forecastSnapshotAt } from '../core/domain';
import { RouteTrack } from '../nav/routeTrack';

// MARK: Terrain lattice

/**
 * Geometry of a terrain sample set: spacing inference, 8-neighbourhood clustering and cell polygons. Works in a local
 * metric plane, so it is robust to grids defined in a projected system (e.g. LV95) that are slightly rotated in WGS84.
 */
export const TerrainLattice = {
  /** Cell size used when the spacing cannot be inferred (single sample). */
  fallbackSpacingMeters: 100,

  /** Median nearest-neighbour distance (m) of up to 64 probe samples; `undefined` for fewer than 2 distinct points. */
  spacing(coordinates: readonly GeoCoordinate[]): number | undefined {
    if (coordinates.length < 2) return undefined;
    const origin = coordinates[0];
    const xy = coordinates.map((c) => localXY(c, origin));
    const step = Math.max(1, Math.floor(xy.length / 64));
    const nearest: number[] = [];
    for (let i = 0; i < xy.length; i += step) {
      let best = Infinity;
      const pi = xy[i];
      for (let j = 0; j < xy.length; j++) {
        if (j === i) continue;
        const dx = pi.x - xy[j].x;
        const dy = pi.y - xy[j].y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > 0.5 && d < best) best = d;
      }
      if (Number.isFinite(best)) nearest.push(best);
    }
    if (nearest.length === 0) return undefined;
    nearest.sort((a, b) => a - b);
    return nearest[Math.floor(nearest.length / 2)];
  },

  /**
   * Connected components of cells sharing the same `group`, where two cells are neighbours when they are at most
   * 1.5 × `spacing` apart (the 8-neighbourhood of a regular grid). Returns index lists, each sorted, in a deterministic
   * order (by the first cell's latitude, then longitude).
   */
  clusters(coordinates: readonly GeoCoordinate[], groups: readonly number[], spacing: number): number[][] {
    if (coordinates.length === 0 || coordinates.length !== groups.length) return [];
    const origin = coordinates[0];
    const threshold = Math.max(spacing, 1) * 1.5;
    const xy = coordinates.map((c) => localXY(c, origin));
    const bucketX = xy.map((p) => Math.floor(p.x / threshold));
    const bucketY = xy.map((p) => Math.floor(p.y / threshold));
    const buckets = new Map<string, number[]>();
    for (let i = 0; i < xy.length; i++) {
      const key = `${bucketX[i]},${bucketY[i]}`;
      const bucket = buckets.get(key);
      if (bucket) bucket.push(i);
      else buckets.set(key, [i]);
    }

    const order = coordinates.map((_, i) => i);
    order.sort((a, b) => {
      const ca = coordinates[a];
      const cb = coordinates[b];
      if (ca.latitude !== cb.latitude) return ca.latitude - cb.latitude;
      if (ca.longitude !== cb.longitude) return ca.longitude - cb.longitude;
      return a - b;
    });
    const visited = new Array<boolean>(coordinates.length).fill(false);
    const components: number[][] = [];
    for (const seed of order) {
      if (visited[seed]) continue;
      visited[seed] = true;
      const queue = [seed];
      let head = 0;
      while (head < queue.length) {
        const current = queue[head];
        head += 1;
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const bucket = buckets.get(`${bucketX[current] + dx},${bucketY[current] + dy}`);
            if (!bucket) continue;
            for (const other of bucket) {
              if (visited[other] || groups[other] !== groups[current]) continue;
              const ddx = xy[other].x - xy[current].x;
              const ddy = xy[other].y - xy[current].y;
              if (Math.sqrt(ddx * ddx + ddy * ddy) <= threshold) {
                visited[other] = true;
                queue.push(other);
              }
            }
          }
        }
      }
      components.push(queue.sort((a, b) => a - b));
    }
    return components;
  },

  /** Coordinate offset by metres east/north (local approximation, fine for cell-sized offsets). */
  offset(c: GeoCoordinate, east: number, north: number): GeoCoordinate {
    return {
      latitude: c.latitude + north / metersPerDegreeLatitude,
      longitude: c.longitude + east / Math.max(1, metersPerDegreeLongitude(c.latitude)),
    };
  },

  /** Corners of the square cell of side `side` centred on `c` (counter-clockwise from south-west). */
  cellCorners(c: GeoCoordinate, side: number): GeoCoordinate[] {
    const h = side / 2;
    const offset = TerrainLattice.offset;
    return [offset(c, -h, -h), offset(c, h, -h), offset(c, h, h), offset(c, -h, h)];
  },
};

// MARK: Polygon overlap

export const PolygonOverlap = {
  /**
   * Whether a ring and a polygon's exterior overlap (vertex containment either way or crossing edges), in lon/lat
   * space — adequate for the sub-10 km shapes compared here.
   */
  intersects(ring: readonly GeoCoordinate[], polygon: GeoPolygon): boolean {
    const a = openRing(ring);
    const b = openRing(polygon.exterior);
    if (a.length < 3 || b.length < 3) return false;
    if (a.some((c) => polygonContains(polygon, c))) return true;
    const zone: GeoPolygon = { exterior: a, holes: [] };
    if (b.some((c) => polygonContains(zone, c))) return true;
    for (let i = 0; i < a.length; i++) {
      const a1 = a[i];
      const a2 = a[(i + 1) % a.length];
      for (let j = 0; j < b.length; j++) {
        if (PolygonOverlap.segmentsIntersect(a1, a2, b[j], b[(j + 1) % b.length])) return true;
      }
    }
    return false;
  },

  segmentsIntersect(p1: GeoCoordinate, p2: GeoCoordinate, q1: GeoCoordinate, q2: GeoCoordinate): boolean {
    const orientation = (a: GeoCoordinate, b: GeoCoordinate, c: GeoCoordinate) =>
      (b.longitude - a.longitude) * (c.latitude - a.latitude) - (b.latitude - a.latitude) * (c.longitude - a.longitude);
    const d1 = orientation(q1, q2, p1);
    const d2 = orientation(q1, q2, p2);
    const d3 = orientation(p1, p2, q1);
    const d4 = orientation(p1, p2, q2);
    return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
  },
};

// MARK: Weather

export const WeatherAdjustment = {
  /** Standard atmosphere lapse rate (°C per metre). */
  lapseRate: 0.0065,

  /** Forecast snapshot at `date` with temperatures adjusted from the forecast elevation to `elevation`. */
  snapshot(
    forecast: WeatherForecast | undefined,
    date: EpochMs,
    elevation: number | undefined,
  ): WeatherSnapshot | undefined {
    if (!forecast) return undefined;
    const found = forecastSnapshotAt(forecast, date);
    if (!found) return undefined;
    const from = forecast.elevation;
    if (from !== undefined && elevation !== undefined && Number.isFinite(from) && Number.isFinite(elevation)) {
      const delta = -(elevation - from) * WeatherAdjustment.lapseRate;
      const adjusted: WeatherSnapshot = { ...found, temperature: found.temperature + delta };
      if (found.feelsLike !== undefined) adjusted.feelsLike = found.feelsLike + delta;
      return adjusted;
    }
    return found;
  },

  /** Temperature adjusted from one elevation to another. */
  temperature(value: number, from: number | undefined, to: number | undefined): number {
    if (from === undefined || to === undefined || !Number.isFinite(from) || !Number.isFinite(to)) return value;
    return value - (to - from) * WeatherAdjustment.lapseRate;
  },
};

// MARK: POI proximity

/** Linear nearest-POI lookup over a small prefetched set (tens to a few hundred POIs). */
export class POIProximity {
  readonly pois: readonly PointOfInterest[];

  constructor(pois: readonly PointOfInterest[]) {
    this.pois = pois.filter((p) => isValidCoordinate(p.coordinate));
  }

  /** Nearest POI within `radius` metres (ties → lower id). */
  nearest(c: GeoCoordinate, radius: number): { poi: PointOfInterest; distance: number } | undefined {
    const dLat = radius / metersPerDegreeLatitude;
    let best: { poi: PointOfInterest; distance: number } | undefined;
    for (const poi of this.pois) {
      if (!(Math.abs(poi.coordinate.latitude - c.latitude) <= dLat)) continue;
      const d = distance(c, poi.coordinate);
      if (!(d <= radius)) continue;
      if (best && (best.distance < d || (best.distance === d && best.poi.id <= poi.id))) continue;
      best = { poi, distance: d };
    }
    return best;
  }
}

// MARK: Categories

export const OvernightFacilities = {
  /** POI categories that can host a night. */
  categories: new Set<POICategory>([
    'camping',
    'mountainHut',
    'bivouacHut',
    'shelter',
    'hotelHostel',
  ]) as ReadonlySet<POICategory>,

  /**
   * Designated facilities get `official`: campsites, mountain huts, bivouac huts, hotels/hostels, and shelters
   * explicitly described as a bivouac hut. Other shelters are resolved like terrain.
   */
  isOfficial(poi: PointOfInterest): boolean {
    switch (poi.category) {
      case 'camping':
      case 'mountainHut':
      case 'bivouacHut':
      case 'hotelHostel':
        return true;
      case 'shelter':
        return poi.attributes.accommodation?.kind === 'bivouacHut';
      default:
        return false;
    }
  },
};

// MARK: Confidence / sensitivity

/** Rank 0 (low) … 2 (high) (Swift `Confidence.rankValue`). */
export function confidenceRankValue(confidence: Confidence): number {
  switch (confidence) {
    case 'low':
      return 0;
    case 'medium':
      return 1;
    case 'high':
      return 2;
  }
}

/**
 * Ordering used to aggregate sensitivities (unknown sits between moderate and high: treat missing data cautiously)
 * (Swift `Sensitivity.aggregationRank`).
 */
export function sensitivityAggregationRank(sensitivity: Sensitivity): number {
  switch (sensitivity) {
    case 'low':
      return 0;
    case 'moderate':
      return 1;
    case 'unknown':
      return 2;
    case 'high':
      return 3;
  }
}

// MARK: Route slice (Swift `extension RouteTrack { func slice(from:to:) }`)

/** Vertices between two distances (inclusive, with interpolated ends), as a new track whose distance 0 is `from`. */
export function sliceTrack(track: RouteTrack, from: number, to: number): RouteTrack | undefined {
  if (track.coordinates.length < 2 || !Number.isFinite(from) || !Number.isFinite(to) || !(to > from)) return undefined;
  const start = track.coordinateAtDistance(from);
  const end = track.coordinateAtDistance(to);
  if (!start || !end) return undefined;
  const routePoint = (coordinate: GeoCoordinate, d: number): RoutePoint => {
    const elevation = track.elevationAtDistance(d);
    return elevation === undefined ? { coordinate } : { coordinate, elevation };
  };
  const points: RoutePoint[] = [routePoint(start, from)];
  const cumulative = track.cumulativeDistance;
  // First vertex strictly after `from` (binary search; Swift scans every vertex).
  let lo = 0;
  let hi = cumulative.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cumulative[mid] <= from) lo = mid + 1;
    else hi = mid;
  }
  for (let index = lo; index < cumulative.length && cumulative[index] < to; index++) {
    points.push(routePoint(track.coordinates[index], cumulative[index]));
  }
  points.push(routePoint(end, to));
  return RouteTrack.fromPolyline({ points }, track.route.id + '#slice');
}

// MARK: Reason equality (Swift `Hashable` synthesis)

/** Value equality of two reason kinds (every payload is a primitive). */
export function reasonKindEquals(a: BivouacReasonKind, b: BivouacReasonKind): boolean {
  if (a === b) return true;
  if (a.kind !== b.kind) return false;
  const ra = a as unknown as Record<string, unknown>;
  const rb = b as unknown as Record<string, unknown>;
  for (const key of Object.keys(ra)) if (ra[key] !== rb[key]) return false;
  for (const key of Object.keys(rb)) if (ra[key] !== rb[key]) return false;
  return true;
}

/** Value equality of two reasons. */
export function reasonEquals(a: BivouacReason, b: BivouacReason): boolean {
  return a.impact === b.impact && a.category === b.category && reasonKindEquals(a.kind, b.kind);
}

/** `reasons.contains(reason)` with value equality. */
export function containsReason(reasons: readonly BivouacReason[], reason: BivouacReason): boolean {
  return reasons.some((r) => reasonEquals(r, reason));
}
